'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Toaster, toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface WanCircuit {
  id: string;
  nome: string;
  tipo: string;
  provider: string;
  banda_download_mbps: number;
  banda_upload_mbps: number;
  ip_pubblico: string;
  gateway: string;
  sede_a_id: string | null;
  sede_b_id: string | null;
  sla_uptime_pct: number;
  contratto_scadenza: string | null;
  costo_mensile: number;
  stato: string;
  note: string;
}

const tipoColors: Record<string, string> = {
  fibra: 'bg-sky-500/20 text-sky-400',
  adsl: 'bg-gray-500/20 text-gray-400',
  vdsl: 'bg-blue-500/20 text-blue-400',
  mpls: 'bg-violet-500/20 text-violet-400',
  vpn: 'bg-green-500/20 text-green-400',
  leased_line: 'bg-amber-500/20 text-amber-400',
  lte: 'bg-orange-500/20 text-orange-400',
  satellite: 'bg-pink-500/20 text-pink-400',
  altro: 'bg-gray-500/20 text-gray-400',
};

const statoColors: Record<string, string> = {
  attivo: 'bg-green-500/20 text-green-400 border-green-500/30',
  inattivo: 'bg-red-500/20 text-red-400 border-red-500/30',
  manutenzione: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
};

const emptyForm = { nome: '', tipo: 'fibra', provider: '', banda_download_mbps: 0, banda_upload_mbps: 0, ip_pubblico: '', gateway: '', sla_uptime_pct: 99.9, contratto_scadenza: '', costo_mensile: 0, stato: 'attivo', note: '' };

export default function WanCircuitsContent() {
  const [circuits, setCircuits] = useState<WanCircuit[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCircuit, setEditingCircuit] = useState<WanCircuit | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchCircuits = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('wan_circuits').select('*').order('nome');
      if (error) throw error;
      setCircuits(data || []);
    } catch (err: any) {
      toast.error('Errore caricamento WAN: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCircuits(); }, [fetchCircuits]);

  const openCreate = () => { setEditingCircuit(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (c: WanCircuit) => {
    setEditingCircuit(c);
    setForm({ nome: c.nome, tipo: c.tipo, provider: c.provider, banda_download_mbps: c.banda_download_mbps, banda_upload_mbps: c.banda_upload_mbps, ip_pubblico: c.ip_pubblico, gateway: c.gateway, sla_uptime_pct: c.sla_uptime_pct, contratto_scadenza: c.contratto_scadenza || '', costo_mensile: c.costo_mensile, stato: c.stato, note: c.note });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.nome) { toast.error('Nome circuito obbligatorio'); return; }
    setSaving(true);
    try {
      const payload = { ...form, contratto_scadenza: form.contratto_scadenza || null };
      if (editingCircuit) {
        const { error } = await supabase.from('wan_circuits').update(payload).eq('id', editingCircuit.id);
        if (error) throw error;
        toast.success('Circuito WAN aggiornato');
      } else {
        const { error } = await supabase.from('wan_circuits').insert(payload);
        if (error) throw error;
        toast.success('Circuito WAN creato');
      }
      setShowModal(false);
      fetchCircuits();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, nome: string) => {
    if (!confirm(`Eliminare il circuito "${nome}"?`)) return;
    try {
      const { error } = await supabase.from('wan_circuits').delete().eq('id', id);
      if (error) throw error;
      toast.success('Circuito eliminato');
      fetchCircuits();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    }
  };

  const totalCosto = circuits.reduce((s, c) => s + c.costo_mensile, 0);
  const attivi = circuits.filter(c => c.stato === 'attivo').length;

  return (
    <div className="flex flex-col h-full bg-background overflow-auto scrollbar-thin">
      <Toaster position="bottom-right" theme="dark" richColors />

      <div className="px-6 py-5 border-b border-border bg-surface flex-shrink-0">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white">Circuiti WAN</h1>
            <p className="text-sm text-secondary-color mt-0.5">Documentazione collegamenti WAN e connettività tra sedi</p>
          </div>
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-lg text-sm font-semibold hover:bg-sky-400 transition-all">
            <Icon name="PlusIcon" size={16} />
            Nuovo Circuito
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4">
          {[
            { label: 'Circuiti Attivi', value: attivi, color: 'text-green-400' },
            { label: 'Circuiti Totali', value: circuits.length, color: 'text-white' },
            { label: 'Costo Mensile', value: `€${totalCosto.toFixed(0)}`, color: 'text-amber-400' },
          ].map(k => (
            <div key={k.label} className="bg-surface-elevated border border-border rounded-lg px-4 py-3">
              <p className="text-xs font-bold text-muted-color uppercase tracking-wide">{k.label}</p>
              <p className={`text-2xl font-bold mt-0.5 ${k.color}`}>{k.value}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : circuits.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <Icon name="GlobeAltIcon" size={40} className="text-muted-color" />
            <p className="text-secondary-color font-semibold">Nessun circuito WAN configurato</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {circuits.map(c => (
              <div key={c.id} className="bg-surface border border-border rounded-xl p-5 hover:border-sky-500/30 transition-all">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-base font-bold text-white">{c.nome}</h3>
                    <p className="text-sm text-secondary-color">{c.provider}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${tipoColors[c.tipo] || 'bg-gray-500/20 text-gray-400'}`}>{c.tipo.replace('_', ' ')}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${statoColors[c.stato] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>{c.stato}</span>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-color font-semibold">Download</span>
                    <span className="text-sm font-mono text-green-400 font-semibold">{c.banda_download_mbps} Mbps</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-color font-semibold">Upload</span>
                    <span className="text-sm font-mono text-sky-400 font-semibold">{c.banda_upload_mbps} Mbps</span>
                  </div>
                  {c.ip_pubblico && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-color font-semibold">IP Pubblico</span>
                      <span className="text-sm font-mono text-secondary-color">{c.ip_pubblico}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-color font-semibold">SLA Uptime</span>
                    <span className="text-sm font-semibold text-secondary-color">{c.sla_uptime_pct}%</span>
                  </div>
                  {c.contratto_scadenza && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-color font-semibold">Scadenza</span>
                      <span className="text-sm text-secondary-color">{new Date(c.contratto_scadenza).toLocaleDateString('it-IT')}</span>
                    </div>
                  )}
                  {c.costo_mensile > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-color font-semibold">Costo/mese</span>
                      <span className="text-sm font-semibold text-amber-400">€{c.costo_mensile.toFixed(0)}</span>
                    </div>
                  )}
                </div>

                {c.note && <p className="text-xs text-muted-color mb-3 p-2 bg-surface-elevated rounded-lg">{c.note}</p>}

                <div className="flex gap-2 pt-3 border-t border-border">
                  <button onClick={() => openEdit(c)} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-surface-elevated border border-border text-secondary-color rounded-lg text-xs font-semibold hover:text-white transition-all">
                    <Icon name="PencilIcon" size={13} />
                    Modifica
                  </button>
                  <button onClick={() => handleDelete(c.id, c.nome)} className="px-3 py-2 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-xs font-semibold hover:bg-red-500/20 transition-all">
                    <Icon name="TrashIcon" size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-surface">
              <h2 className="text-lg font-bold text-white">{editingCircuit ? 'Modifica Circuito WAN' : 'Nuovo Circuito WAN'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-surface-elevated transition-colors">
                <Icon name="XMarkIcon" size={18} className="text-muted-color" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Nome *</label>
                  <input type="text" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Tipo</label>
                  <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50">
                    {Object.keys(tipoColors).map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Provider</label>
                  <input type="text" value={form.provider} onChange={e => setForm({ ...form, provider: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Stato</label>
                  <select value={form.stato} onChange={e => setForm({ ...form, stato: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50">
                    <option value="attivo">Attivo</option>
                    <option value="inattivo">Inattivo</option>
                    <option value="manutenzione">Manutenzione</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Download (Mbps)</label>
                  <input type="number" value={form.banda_download_mbps} onChange={e => setForm({ ...form, banda_download_mbps: parseFloat(e.target.value) || 0 })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Upload (Mbps)</label>
                  <input type="number" value={form.banda_upload_mbps} onChange={e => setForm({ ...form, banda_upload_mbps: parseFloat(e.target.value) || 0 })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">IP Pubblico</label>
                  <input type="text" value={form.ip_pubblico} onChange={e => setForm({ ...form, ip_pubblico: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50 font-mono" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Gateway</label>
                  <input type="text" value={form.gateway} onChange={e => setForm({ ...form, gateway: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50 font-mono" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">SLA Uptime (%)</label>
                  <input type="number" step="0.1" value={form.sla_uptime_pct} onChange={e => setForm({ ...form, sla_uptime_pct: parseFloat(e.target.value) || 99.9 })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Costo Mensile (€)</label>
                  <input type="number" value={form.costo_mensile} onChange={e => setForm({ ...form, costo_mensile: parseFloat(e.target.value) || 0 })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Scadenza Contratto</label>
                <input type="date" value={form.contratto_scadenza} onChange={e => setForm({ ...form, contratto_scadenza: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Note</label>
                <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} rows={2} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50 resize-none" />
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-border sticky bottom-0 bg-surface">
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2.5 border border-border text-secondary-color rounded-lg text-sm font-semibold hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2.5 bg-sky-500 text-white rounded-lg text-sm font-semibold hover:bg-sky-400 transition-all disabled:opacity-50">
                {saving ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
